/*
 *  -----------------------------------------------------------------------
 *    <copyright file="step2-tri-operation-trend.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import FormControl from '@mui/material/FormControl';
import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'hooks';
import { PanelThresholdType } from 'types/graphql-global-types';
import { validateFields } from 'utils/validation';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { FieldSelector, Formats } from 'views/components/central-monitoring/field-selector';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import PanelService from 'views/components/central-monitoring/panels/add-panel/service';
import OutlinedWrapper from 'views/components/core/outlined-wrapper';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';
import 'views/components/central-monitoring/panels/add-panel/styles.scss';

const Step2TriOperationalTrendModel = forwardRef(
    ({ show, panelState, updateErrorState, setPage, page, updateState, canSave }: any, ref) => {
        const { t } = useTranslation();
        const [thresholdTypeValue, setThresholdTypeValue] = useState('');
        const [thresholdTypeError, setThresholdTypeError] = useState(null);
        const initialState = {
            dataSource: panelState.dataSource.dataSource,
            yAxisDataField: panelState.dataSource.yAxisDataField,
            sortField: panelState.dataSource.sortField,
            dataInclusionUnit: `${panelState.modelParams.dataInclusionUnit}`,
            dataInclusionPeriod: panelState.modelParams.dataInclusionPeriod,
            studyComparison: panelState.modelParams.studyComparison,
            triThreshold1Type: panelState.modelParams.triThreshold1Type,
            triThreshold2Type: panelState.modelParams.triThreshold2Type,
            thresholdNum1: panelState.modelParams.thresholdNum1,
            thresholdNum2: panelState.modelParams.thresholdNum2,
        };
        const initialErrors = {
            dataSource: '',
            yAxisDataField: '',
            sortField: '',
            dataInclusionUnit: '',
            dataInclusionPeriod: '',
            studyComparison: '',
            triThreshold1Type: '',
            triThreshold2Type: '',
            thresholdNum1: '',
            thresholdNum2: '',
        };
        const [state, setState] = useState({ ...initialState });
        const [errors, setErrors] = useState({ ...initialErrors } as any);
        const { formats, dataInclusionPeriods, studyComparison, thresholdType } = PanelConstants;

        useEffect(() => {
            setThresholdTypeValue(
                PanelService.getThresholdType(
                    panelState.modelParams.triThreshold1Type,
                    panelState.modelParams.triThreshold2Type,
                ),
            );
        }, [panelState]);

        const dataInclusionUnits = Array.from(Array(30).keys()).map((item: number) => ({
            id: `${item + 1}`,
            text: `${item + 1}`,
        }));
        dataInclusionUnits.unshift({
            id: '',
            text: t('ra:selectValue'),
        });

        const onChangeThreshold = () => (event: any) => {
            const { value } = event.target;

            setThresholdTypeError(null);

            let firstValue = '';
            let secondValue = '';

            switch (value) {
                case PanelConstants.panelThresholdType.SAMPLE_MEDIAN_MEAN:
                    firstValue = PanelThresholdType.SAMPLE_MEDIAN;
                    secondValue = PanelThresholdType.SAMPLE_MEAN;
                    break;
                case PanelConstants.panelThresholdType.STUDY_MEDIAN_MEAN:
                    firstValue = PanelThresholdType.STUDY_MEDIAN;
                    secondValue = PanelThresholdType.STUDY_MEAN;
                    break;
                default:
                    firstValue = PanelThresholdType.STUDY_SPECIFIC;
                    secondValue = PanelThresholdType.STUDY_SPECIFIC;
                    break;
            }

            setThresholdTypeValue(value);
            updateState({ target: { name: 'modelParams.triThreshold1Type', value: firstValue } });
            updateState({ target: { name: 'modelParams.triThreshold2Type', value: secondValue } });

            setState({
                ...state,
                triThreshold1Type: firstValue,
                triThreshold2Type: secondValue,
                thresholdNum1: value !== PanelThresholdType.STUDY_SPECIFIC ? 0 : state.thresholdNum1,
                thresholdNum2: value !== PanelThresholdType.STUDY_SPECIFIC ? 0 : state.thresholdNum2,
            });

            if (value !== PanelThresholdType.STUDY_SPECIFIC)
                setErrors({
                    ...errors,
                    thresholdNum1: null,
                    thresholdNum2: null,
                });
        };

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            let error = {};

            if (
                thresholdTypeValue === PanelThresholdType.STUDY_SPECIFIC &&
                (field === 'thresholdNum1' || field === 'thresholdNum2')
            ) {
                if (field === 'thresholdNum1') {
                    if (value >= state.thresholdNum2)
                        error = {
                            thresholdNum1: t('admin:redThresholdShouldBeBiggerThanAmber'),
                            thresholdNum2: t('admin:redThresholdShouldBeBiggerThanAmber'),
                        };
                    else {
                        error = { thresholdNum1: '', thresholdNum2: '' };
                    }
                }
                if (field === 'thresholdNum2') {
                    if (value <= state.thresholdNum1) {
                        error = {
                            thresholdNum1: t('admin:redThresholdShouldBeBiggerThanAmber'),
                            thresholdNum2: t('admin:redThresholdShouldBeBiggerThanAmber'),
                        };
                    } else {
                        error = { thresholdNum1: '', thresholdNum2: '' };
                    }
                }
            } else {
                error = validateFields(value, field, length, required);
            }
            setErrors({
                ...errors,
                ...error,
            });
        };

        const onChange = (
            fieldName: string,
            length?: number,
            required?: boolean,
            key?: string,
            value?: any,
            isNumber = true,
        ) => {
            if (fieldName === 'dataSource')
                setState({
                    ...state,
                    [fieldName]: isNumber ? PanelService.checkIfNumber(value) : value,
                    yAxisDataField: '',
                    sortField: '',
                });
            else
                setState({
                    ...state,
                    [fieldName]: value !== '' && isNumber ? PanelService.checkIfNumber(value) : value,
                });
            updateState({
                target: { name: key || fieldName, value: isNumber ? PanelService.checkIfNumber(value) : value },
            });

            validate(value, fieldName, length, required);
        };

        const onChangeNumber = (fieldName: string, key?: string, min?: number, errorText?: string) => (event: any) => {
            let error = { [fieldName]: '' };
            const { value } = event.target;
            setState({
                ...state,
                [fieldName]: +value,
            });
            if (min !== undefined && value <= min) error = { [fieldName]: errorText };

            setErrors({
                ...errors,
                ...error,
            });

            updateState({ target: { name: key || fieldName, value: +value } });
            validate(value, fieldName);
        };

        const checkRequiredFields = (fields: string[]) => {
            let isValid = true;
            let newErrors = { ...errors };
            fields.forEach((field) => {
                let value = (state as any)[field];
                if (typeof value === 'number') value = `${value}`;
                let error = {};
                if ((field === 'thresholdNum2' || field === 'thresholdNum1') && value <= 0) {
                    error = { [field]: t('admin:stableValueWarning', { min: 0 }) };
                } else {
                    error = validateFields(value, field, 0, true);
                }

                newErrors = {
                    ...newErrors,
                    ...error,
                };
            });

            if (Object.keys(newErrors).filter((item) => (newErrors as any)[item]).length > 0) isValid = false;

            const error = thresholdTypeValue === '' ? t('ra:requiredFieldError') : null;
            if (error) isValid = false;
            setThresholdTypeError(error);
            setErrors({ ...newErrors });
            return isValid;
        };

        const validationOnUpdate = () => {
            const requiredFields = [
                'dataSource',
                'yAxisDataField',
                'sortField',
                'dataInclusionUnit',
                'dataInclusionPeriod',
                'studyComparison',
            ];

            if (thresholdTypeValue === PanelThresholdType.STUDY_SPECIFIC)
                requiredFields.splice(0, 0, 'thresholdNum1', 'thresholdNum2');

            return checkRequiredFields(requiredFields);
        };

        const onNext = () => {
            if (validationOnUpdate()) setPage({ ...page, number: 3, available: 3 });
            else if (page.number !== 2) setPage({ ...page, number: 2, available: page.available });
        };

        const cleanStep = () => {
            setState({ ...initialState });
            setErrors({ ...initialErrors });
        };

        useEffect(() => {
            updateErrorState(errors, 2);
        }, [errors]);

        useImperativeHandle(ref, () => ({
            nextButton() {
                onNext();
            },
            cleanStep() {
                cleanStep();
            },
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        return (
            <div style={!show ? { display: 'none' } : {}}>
                <div className="type-edc-wrapper mt mobile-prefix-30 mb-30">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <DataSourceSelector
                            label={t('admin:rowData')}
                            onChange={(value: string) => {
                                onChange('dataSource', null, false, 'dataSource.dataSource', value);
                            }}
                            fieldSelect="dataSource"
                            value={state.dataSource}
                            labelField="rowDataSource"
                            showDefault={false}
                            error={errors.dataSource}
                            customClassName="edit-atts-category"
                            searchable
                            tag="PANEL"
                            disabled={!canSave}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <FieldSelector
                            customClassName="add-panel-flagField"
                            fieldSelect="yAxisDataField"
                            onChange={(value: string) => {
                                onChange('yAxisDataField', null, false, 'dataSource.yAxisDataField', value);
                            }}
                            label={t('admin:yAxisData')}
                            labelField="yAxisDataField"
                            customId="y-axis-data-field"
                            value={state.yAxisDataField}
                            error={errors.yAxisDataField}
                            dataSource={state.dataSource}
                            formats={[Formats.Int, Formats.Float]}
                            searchable
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mb-30">
                    <FormControl variant="outlined" className="form-control-type not-full">
                        <FieldSelector
                            customClassName="add-panel-column-dateField"
                            fieldSelect="sortField"
                            onChange={(value: string) => {
                                onChange('sortField', null, false, 'dataSource.sortField', value);
                            }}
                            label={t('admin:sortByDate')}
                            labelField="sortField"
                            customId="add-panel-date-field"
                            value={state.sortField}
                            error={errors.sortField}
                            dataSource={state.dataSource}
                            formats={[formats.Date, formats.PartialDate]}
                            searchable
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
                <OutlinedWrapper customClass="mb" label={t('admin:dataInclusionPeriod')}>
                    <div className="type-edc-wrapper">
                        <FormControl variant="outlined" className="form-control-type mr">
                            <SelectWithCategories
                                customClassName="edit-control-review-frequency-units"
                                showWarning={false}
                                fieldSelect="dataInclusionUnit"
                                label={t('admin:unit')}
                                onChange={(fieldName?: string) => (event: any) => {
                                    onChange(
                                        fieldName,
                                        null,
                                        false,
                                        'modelParams.dataInclusionUnit',
                                        event.target?.value,
                                    );
                                }}
                                filterable={false}
                                labelField="dataInclusionUnit"
                                dataArray={dataInclusionUnits}
                                customId="data-inclusion-unit"
                                currentState={state.dataInclusionUnit.toString()}
                                error={errors.dataInclusionUnit}
                            />
                        </FormControl>
                        <FormControl variant="outlined" className="form-control-type">
                            <SelectWithCategories
                                customClassName="edit-control-review-frequency-units"
                                showWarning={false}
                                fieldSelect="dataInclusionPeriod"
                                onChange={(fieldName?: string) => (event: any) => {
                                    onChange(
                                        fieldName,
                                        null,
                                        false,
                                        'modelParams.dataInclusionPeriod',
                                        event.target?.value,
                                    );
                                }}
                                label={t('admin:period')}
                                filterable={false}
                                labelField="dataInclusionPeriod"
                                dataArray={dataInclusionPeriods}
                                customId="data-inclusion-period"
                                currentState={state.dataInclusionPeriod}
                                error={errors.dataInclusionPeriod}
                            />
                        </FormControl>
                    </div>
                </OutlinedWrapper>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <SelectWithCategories
                            customClassName="edit-control-review-frequency-units"
                            showWarning={false}
                            fieldSelect="studyComparison"
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(fieldName, null, false, 'modelParams.studyComparison', event.target?.value);
                            }}
                            label={t('admin:studyComparison')}
                            filterable={false}
                            labelField="studyComparison"
                            dataArray={studyComparison}
                            customId="study-comparison"
                            currentState={state.studyComparison}
                            error={errors.studyComparison}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <SelectWithCategories
                            customClassName="edit-control-review-frequency-units"
                            showWarning={false}
                            fieldSelect="thresholdTypeValue"
                            onChange={onChangeThreshold}
                            label={t('admin:thresholdType')}
                            filterable={false}
                            labelField="thresholdTypeValue"
                            dataArray={thresholdType}
                            customId="threshold-type-value"
                            currentState={thresholdTypeValue}
                            error={thresholdTypeError}
                        />
                    </FormControl>
                </div>
                {thresholdTypeValue === PanelThresholdType.STUDY_SPECIFIC ? (
                    <OutlinedWrapper customClass="mb" label={t('admin:thresholds')}>
                        <div className="type-edc-wrapper no-mb">
                            <FormControl variant="outlined" className="form-control-type mr">
                                <TextField
                                    label={t('admin:amberThresholdValue')}
                                    id="amber-threshold-value"
                                    fullWidth
                                    value={state.thresholdNum1}
                                    onChange={onChangeNumber(
                                        'thresholdNum1',
                                        'modelParams.thresholdNum1',
                                        0,
                                        t('admin:stableValueWarning', { min: 0 }),
                                    )}
                                    placeholder={t('enterValue')}
                                    helperText={
                                        <span
                                            style={errors.thresholdNum1 ? { color: '#ae0015' } : {}}
                                            className="remaining-text"
                                        >
                                            {errors.thresholdNum1 ? errors.thresholdNum1 : t('admin:numericValue')}
                                        </span>
                                    }
                                    variant="outlined"
                                    type="number"
                                    color="secondary"
                                    className="before-select"
                                    error={!!errors.thresholdNum1}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type">
                                <TextField
                                    label={t('admin:redThresholdValue')}
                                    id="red-threshold-value"
                                    fullWidth
                                    value={state.thresholdNum2}
                                    onChange={onChangeNumber(
                                        'thresholdNum2',
                                        'modelParams.thresholdNum2',
                                        0,
                                        t('admin:stableValueWarning', { min: 0 }),
                                    )}
                                    placeholder={t('enterValue')}
                                    helperText={
                                        <span
                                            style={errors.thresholdNum2 ? { color: '#ae0015' } : {}}
                                            className="remaining-text"
                                        >
                                            {errors.thresholdNum2 ? errors.thresholdNum2 : t('admin:numericValue')}
                                        </span>
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    type="number"
                                    className="before-select"
                                    error={!!errors.thresholdNum2}
                                />
                            </FormControl>
                        </div>
                    </OutlinedWrapper>
                ) : null}
            </div>
        );
    },
);

export default Step2TriOperationalTrendModel;
